import { useState, useRef, useMemo, useEffect } from "react";
import { Check, Plus, Search, X } from "lucide-react";
import * as Popover from "@radix-ui/react-popover";
import { TagBadge } from "@/components/ui/Tag/TagBadge";
import { TAG_COLORS } from "@/components/ui/Tag/types";
import { useDocumentTags, useCreateDocumentTag } from "@/hooks/use-documents";

interface UploadTagInputProps {
  selectedTagIds: string[];
  onAdd: (tagId: string) => void;
  onRemove: (tagId: string) => void;
  disabled?: boolean;
}

export function UploadTagInput({
  selectedTagIds,
  onAdd,
  onRemove,
  disabled,
}: UploadTagInputProps) {
  const { tags: availableTags } = useDocumentTags();
  const createTag = useCreateDocumentTag();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);

  const selectedTags = useMemo(
    () => availableTags.filter((t) => selectedTagIds.includes(t.id)),
    [availableTags, selectedTagIds],
  );

  const filteredTags = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return availableTags;
    return availableTags.filter((t) => t.name.toLowerCase().includes(q));
  }, [availableTags, query]);

  const exactMatch = useMemo(
    () =>
      availableTags.some(
        (t) => t.name.toLowerCase() === query.trim().toLowerCase(),
      ),
    [availableTags, query],
  );

  const handleToggle = (tagId: string) => {
    if (selectedTagIds.includes(tagId)) {
      onRemove(tagId);
    } else {
      onAdd(tagId);
    }
    setQuery("");
    inputRef.current?.focus();
  };

  const handleCreate = async () => {
    const name = query.trim();
    if (!name) return;
    const color = TAG_COLORS[availableTags.length % TAG_COLORS.length];
    const newTagId = await createTag({ name, color });
    onAdd(String(newTagId));
    setQuery("");
    inputRef.current?.focus();
  };

  useEffect(() => {
    if (open) {
      const timer = setTimeout(() => inputRef.current?.focus(), 50);
      return () => clearTimeout(timer);
    }
    setQuery("");
  }, [open]);

  // Read-only during upload
  if (disabled) {
    return selectedTags.length > 0 ? (
      <div className="flex flex-wrap items-center gap-1">
        {selectedTags.map((tag) => (
          <TagBadge key={tag.id} name={tag.name} color={tag.color} />
        ))}
      </div>
    ) : null;
  }

  return (
    <Popover.Root open={open} onOpenChange={setOpen}>
      <Popover.Anchor asChild>
        <div className="flex flex-wrap items-center gap-1.5">
          {selectedTags.map((tag) => (
            <TagBadge
              key={tag.id}
              name={tag.name}
              color={tag.color}
              onRemove={() => onRemove(tag.id)}
            />
          ))}

          <Popover.Trigger asChild>
            <button
              type="button"
              className="inline-flex items-center gap-1 h-[22px] px-2 text-[11px] text-muted hover:text-secondary border border-dashed border-border hover:border-muted-foreground/40 rounded-full transition-colors cursor-pointer"
            >
              <Plus className="h-3 w-3" />
              {selectedTags.length === 0 ? "Add tags" : "Add"}
            </button>
          </Popover.Trigger>
        </div>
      </Popover.Anchor>
      <Popover.Portal>
        <Popover.Content
          align="start"
          sideOffset={6}
          className="bg-card rounded-xl border border-border shadow-lg z-50 w-[240px] animate-in fade-in-0 zoom-in-95"
          onOpenAutoFocus={(e) => e.preventDefault()}
        >
          {/* Search */}
          <div className="flex items-center gap-2 px-3 py-2 border-b border-border">
            <Search className="h-3.5 w-3.5 text-muted shrink-0" />
            <input
              ref={inputRef}
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && query.trim() && !exactMatch) {
                  handleCreate();
                }
              }}
              placeholder="Search or create..."
              className="flex-1 bg-transparent text-sm text-primary placeholder:text-muted outline-none"
            />
            {query && (
              <button
                type="button"
                onClick={() => setQuery("")}
                className="text-muted hover:text-secondary"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            )}
          </div>

          {/* Tag list */}
          <div className="max-h-[200px] overflow-y-auto overscroll-contain p-1 space-y-0.5">
            {filteredTags.length > 0 ? (
              filteredTags.map((tag) => {
                const isSelected = selectedTagIds.includes(tag.id);
                return (
                  <button
                    key={tag.id}
                    type="button"
                    onClick={() => handleToggle(tag.id)}
                    className={`w-full flex items-center gap-2.5 px-3 py-1.5 text-[13px] rounded-lg transition-colors cursor-pointer text-left ${
                      isSelected
                        ? "bg-accent/10 text-primary"
                        : "text-secondary hover:text-primary hover:bg-card-hover"
                    }`}
                  >
                    <span
                      className="w-2.5 h-2.5 rounded-full shrink-0"
                      style={{ backgroundColor: tag.color }}
                    />
                    <span className="flex-1 truncate">{tag.name}</span>
                    {isSelected && (
                      <Check className="h-3.5 w-3.5 shrink-0 text-accent" />
                    )}
                  </button>
                );
              })
            ) : (
              <div className="px-3 py-3 text-center text-[13px] text-muted">
                {query.trim() ? "No matching tags" : "No tags available"}
              </div>
            )}
          </div>

          {/* Create option */}
          {query.trim() && !exactMatch && (
            <div className="border-t border-border p-1">
              <button
                type="button"
                onClick={handleCreate}
                className="w-full flex items-center gap-2 px-3 py-1.5 text-[13px] text-accent hover:bg-card-hover rounded-lg transition-colors cursor-pointer"
              >
                <Plus className="h-3.5 w-3.5" />
                Create &quot;{query.trim()}&quot;
              </button>
            </div>
          )}
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
}
